import { useShallow } from "zustand/react/shallow";
import type { StoreApi, UseBoundStore } from "zustand";

/**
 * Creates a shallow version of a Zustand store hook that enables selective property subscriptions.
 * Only triggers re-renders when the selected properties change, not when any property in the store changes.
 *
 * @template T - The type of the store state
 * @param useStoreHook - A Zustand store hook (e.g., useSidebarStore)
 * @returns A new hook that accepts property keys and returns only those properties
 *
 * @example
 * // Create a shallow version of your store
 * const useSidebarShallowStore = useShallowStore(useSidebarStore);
 *
 * // In your component - only re-renders when isCollapsed changes
 * const { isCollapsed } = useSidebarShallowStore('isCollapsed');
 *
 * // Select multiple properties - only re-renders when either changes
 * const { isCollapsed, activeTab } = useSidebarShallowStore('isCollapsed', 'activeTab');
 *
 * // Without this hook, useSidebarStore() would re-render on ANY store change
 */
export const createShallowStore = <T>(useStoreHook: UseBoundStore<StoreApi<T>>) => {
  return function useShallowSelector<K extends keyof T>(...keys: [K, ...K[]]): Pick<T, K> {
    return useStoreHook(
      useShallow((state) => {
        const result = {} as Pick<T, K>;
        keys.forEach((key) => {
          result[key] = state[key];
        });
        return result;
      })
    );
  };
};
